/**
 * Teams API Contract
 *
 * ts-rest contract for Works Lite teams endpoints.
 * Covers CRUD operations, member management, and utilization tracking.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { SAFE_TEXT_PATTERN, SAFE_TEXT_VALIDATION_MSG } from '../shared/validation.js';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

export const teamMemberSchema = z.object({
  id: z.number(),
  userId: z.number(),
  name: z.string(),
  role: z.string().nullable(),
  taskId: z.number().nullable(),
});

/**
 * Team utilisation for an explicit time period (CWL-157).
 *
 * `rate` is `bookedHours / capacityHours * 100` — the scheduled job hours a
 * team has booked in the window, against the working hours its active members
 * had available. Both sides are returned so the UI can show what the figure is
 * made of rather than a bare percentage, and so the period it covers is never
 * ambiguous.
 *
 * `capacityHours` of 0 (no members, everyone on leave, or a window that falls
 * entirely outside working hours) means the rate is not meaningful — callers
 * should render "no capacity" rather than "0% utilised".
 */
export const teamUtilizationSchema = z.object({
  /** Percentage to 1dp. Can exceed 100 when a team is over-booked. */
  rate: z.number(),
  /** Scheduled job hours falling inside the period, clipped to its bounds. */
  bookedHours: z.number(),
  /** Working hours the team's active members had available in the period. */
  capacityHours: z.number(),
  /** Distinct jobs contributing to `bookedHours`. */
  jobCount: z.number(),
  /** Period start, inclusive (ISO 8601). */
  periodStart: z.string(),
  /** Period end, exclusive (ISO 8601). */
  periodEnd: z.string(),
});

/**
 * Optional period bounds for the utilisation figure. Omitting both yields the
 * current week (Monday 00:00 to the following Monday 00:00).
 */
export const utilizationPeriodQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

export const teamListItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  maxCapacity: z.number().nullable(),
  memberCount: z.number(),
  activeJobs: z.number(),
  completedJobs: z.number(),
  utilization: teamUtilizationSchema,
  members: z.array(z.object({ id: z.number(), name: z.string() })),
});

export const teamDetailSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  maxCapacity: z.number().nullable(),
  memberCount: z.number(),
  activeJobs: z.number(),
  completedJobs: z.number(),
  utilization: teamUtilizationSchema,
  // The team lead's user id (team.team_lead_id). Null when no lead is set.
  teamLeadId: z.number().nullable(),
  members: z.array(teamMemberSchema),
  // Geographic coverage driving availability matching. areaIds = specific
  // areas the team covers; regionIds = whole regions it covers.
  areaIds: z.array(z.number()),
  regionIds: z.array(z.number()),
});

export const createTeamBodySchema = z.object({
  name: z.string().min(1).max(100).regex(SAFE_TEXT_PATTERN, SAFE_TEXT_VALIDATION_MSG),
  description: z
    .string()
    .max(500)
    .refine((v) => v === '' || SAFE_TEXT_PATTERN.test(v), SAFE_TEXT_VALIDATION_MSG)
    .optional(),
  maxCapacity: z.number().int().min(1).max(500).optional(),
  memberIds: z.array(z.number()).optional(),
  // Optional team lead. When set, must be one of the memberIds — enforced
  // server-side (a lead is always an active member of the team).
  teamLeadId: z.number().int().optional(),
  // Geographic coverage. Without at least one area or region the team is not
  // matched by availability lookups.
  areaIds: z.array(z.number()).optional(),
  regionIds: z.array(z.number()).optional(),
});

export const updateTeamBodySchema = z.object({
  name: z.string().min(1).max(100).regex(SAFE_TEXT_PATTERN, SAFE_TEXT_VALIDATION_MSG).optional(),
  description: z
    .string()
    .max(500)
    .refine((v) => v === '' || SAFE_TEXT_PATTERN.test(v), SAFE_TEXT_VALIDATION_MSG)
    .nullable()
    .optional(),
  maxCapacity: z.number().int().min(1).max(500).nullable().optional(),
  // Reassign (or clear, via null) the team lead. A non-null lead must be an
  // active member of the team — enforced server-side.
  teamLeadId: z.number().int().nullable().optional(),
  // When provided, replaces the team's area / region coverage.
  areaIds: z.array(z.number()).optional(),
  regionIds: z.array(z.number()).optional(),
});

export const addTeamMemberBodySchema = z.object({
  userId: z.number(),
  role: z.string().max(100).optional(),
});

export const teamsContract = c.router({
  listTeams: {
    method: 'GET',
    path: '/',
    // from/to scope the utilization figure; see utilizationPeriodQuerySchema.
    query: utilizationPeriodQuerySchema,
    responses: {
      200: z.array(teamListItemSchema),
      500: errorResponseSchema,
    },
    summary: 'List all teams with member counts and period utilization',
  },

  getTeam: {
    method: 'GET',
    path: '/:id',
    pathParams: z.object({ id: z.string() }),
    query: utilizationPeriodQuerySchema,
    responses: {
      200: teamDetailSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get team details with full member list',
  },

  createTeam: {
    method: 'POST',
    path: '/',
    body: createTeamBodySchema,
    responses: {
      201: teamDetailSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Create a new team',
  },

  updateTeam: {
    method: 'PATCH',
    path: '/:id',
    pathParams: z.object({ id: z.string() }),
    body: updateTeamBodySchema,
    responses: {
      200: teamDetailSchema,
      // 400: teamLeadId references a non-member — see INVALID_TEAM_LEAD code.
      400: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update team details',
  },

  deleteTeam: {
    method: 'DELETE',
    path: '/:id',
    pathParams: z.object({ id: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ success: z.boolean() }),
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Soft-delete a team',
  },

  getTeamMembers: {
    method: 'GET',
    path: '/:id/members',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: z.array(teamMemberSchema),
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get all members of a team',
  },

  addTeamMember: {
    method: 'POST',
    path: '/:id/members',
    pathParams: z.object({ id: z.string() }),
    body: addTeamMemberBodySchema,
    responses: {
      201: teamMemberSchema,
      400: errorResponseSchema,
      404: errorResponseSchema,
      409: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Add a member to a team',
  },

  removeTeamMember: {
    method: 'DELETE',
    path: '/:id/members/:userId',
    pathParams: z.object({ id: z.string(), userId: z.string() }),
    // When the member being removed is the current team lead, the caller must
    // supply newTeamLeadId so leadership is reassigned atomically. The new lead
    // must be another active member of the team.
    query: z.object({ newTeamLeadId: z.coerce.number().optional() }),
    body: c.noBody(),
    responses: {
      200: z.object({ success: z.boolean() }),
      // 400: lead removal needs a (valid) replacement — see error code.
      400: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Remove a member from a team (reassigning leadership if required)',
  },
});

export type TeamUtilization = z.infer<typeof teamUtilizationSchema>;
export type UtilizationPeriodQuery = z.infer<typeof utilizationPeriodQuerySchema>;
export type TeamListItem = z.infer<typeof teamListItemSchema>;
export type TeamDetail = z.infer<typeof teamDetailSchema>;
export type TeamMemberData = z.infer<typeof teamMemberSchema>;
export type CreateTeamBody = z.infer<typeof createTeamBodySchema>;
export type UpdateTeamBody = z.infer<typeof updateTeamBodySchema>;
export type AddTeamMemberBody = z.infer<typeof addTeamMemberBodySchema>;
